import React, {
  useRef, useReducer, useEffect, useState,
} from 'react';
import { Input, Label } from '@americanexpress/dls-react';
import PropTypes from 'prop-types';
import { validateDlsInput } from '../../Validations/Validations';

const SplitInput = (props) => {
  const {
    fieldLabel,
    fieldId,
    inputs,
    validations = [],
    cssProperties,
    validationErrors,
    setValidationErrors,
    fieldRank = 1,
    fieldValidation,
    setFocus,
    formSubmissionPayload,
  } = props;
  const [data, setData] = useState(null);
  const inputRefs = useRef([]);
  const [validationErrState, dispatchErrState] = useReducer(
    (prevState, payload) => ({ ...prevState, ...payload }),
    { validationErr: '', isValid: true }
  );

  useEffect(() => {
    // validationErr also can change multiple times, so need one isValid
    if (validationErrState.isValid && validationErrors[fieldRank]) {
      delete validationErrors[fieldRank];
      setValidationErrors({ ...validationErrors });
    } else if (!validationErrState.isValid) {
      setValidationErrors({
        ...validationErrors,
        ...{ [fieldRank]: `${fieldId}-1` },
      });
    }
  }, [validationErrState.isValid]);

  useEffect(() => {
    if (formSubmissionPayload && formSubmissionPayload[fieldId]) {
      const value = formSubmissionPayload[fieldId];
      setData(value);
      let startIndex = 0;
      inputs.forEach((input, index) => {
        inputRefs.current[index].value = value.slice(startIndex, startIndex + input.length);
        startIndex += input.length;
      });
    }
  }, [fieldId]);

  const handleOnChange = () => {
    const combinedValue = inputRefs.current.map((ref) => ref.value).join('');
    if (validations.length > 0) {
      const invalidMsg = validateDlsInput(combinedValue, validations);
      if (invalidMsg) {
        dispatchErrState({ validationErr: `${invalidMsg}`, isValid: false });
      } else {
        dispatchErrState({ validationErr: '', isValid: true });
      }
    }
    setData(combinedValue);
  };

  const handleKeyDown = (e, index) => {
    const defaultAllowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];
    // if key is one of the defaultAllowedLeys then it's valid
    if (defaultAllowedKeys.includes(e.key)) {
      if (e.key === 'Backspace' && e.target.value === '') {
        inputRefs.current?.[index - 1]?.focus();
      }
    } else if (inputs[index].allowedKeysRegex) {
      const isKeyValid = inputs[index].allowedKeysRegex
        ? new RegExp(inputs[index].allowedKeysRegex).test(e.key)
        : true;
      if (isKeyValid) {
        if (e.target.value.length === inputs[index].length) {
          // curent input is full and
          // if next input is not full then focus on next input
          const nextInput = inputRefs.current[index + 1];
          if (nextInput && nextInput.value.length < inputs[index + 1]?.length) {
            nextInput?.focus();
          } else {
            e.preventDefault();
          }
        }
      } else {
        e.preventDefault();
      }
    }
  };

  const handleInvalid = (e) => {
    if (validations.length > 0) {
      e.preventDefault();
      handleOnChange();
      setFocus({ ...validationErrors, ...{ [fieldRank]: `${fieldId}-1` } });
    }
  };
  /* eslint-disable react/no-array-index-key -- need it temporarly */
  return (
    <>
      {
        fieldLabel?.label && (
          <Label htmlFor={fieldId} className={fieldLabel.cssProperties?.style}>
            {fieldLabel.label}
            {
              !fieldLabel.required && (
                <span>
                  {fieldLabel.optionalLabel ? fieldLabel.optionalLabel : ' (Optional)'}
                </span>
              )
            }
          </Label>
        )
      }
      <div className="row">
        {inputs.map((input, index) => (
          <div key={index} className={input.style}>
            <Input
              style={{
                border: '.0625rem solid #8e9092',
                textAlign: 'center',
              }}
              id={`${fieldId}-${index + 1}`}
              ref={(el) => { inputRefs.current[index] = el; }}
              name={`${fieldId}-${index + 1}`}
              onChange={(e) => handleOnChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onInvalid={(e) => handleInvalid(e)}
              minLength={input.length}
              required={fieldValidation.required}
            />
          </div>
        ))}
      </div>
      <Input type="hidden" id={fieldId} name={fieldId} value={data} />
      {validationErrState.validationErr && (
        <div
          id="alert-error"
          className={
            cssProperties.validationErrorStyle
              ? cssProperties.validationErrorStyle
              : 'alert dls-color-warning pad-1-t col-md-12'
          }
        >
          <span className="icon icon-sm dls-icon-warning-filled pad-1-r" />
          <span>{validationErrState.validationErr}</span>
        </div>
      )}
    </>
  );
};
/* eslint-enable react/no-array-index-key -- need it temporarly */
SplitInput.propTypes = {
  fieldLabel: PropTypes.objectOf(PropTypes.shape),
  fieldId: PropTypes.string,
  inputs: PropTypes.arrayOf(PropTypes.shape),
  validations: PropTypes.arrayOf(PropTypes.shape),
  cssProperties: PropTypes.objectOf(PropTypes.shape),
  validationErrors: PropTypes.objectOf(PropTypes.string),
  setValidationErrors: PropTypes.func,
  fieldRank: PropTypes.number,
  fieldValidation: PropTypes.objectOf(PropTypes.shape),
  setFocus: PropTypes.func,
  formSubmissionPayload: PropTypes.objectOf(PropTypes.shape),
};

export default SplitInput;
